import { AudioScrubber, type ScrubVoice } from "./audio-scrubber"
import { captureFrameFromCorsVideo, VIDEO_CAPTURE_UNAVAILABLE } from "./capture-video-frame"
import {
  clipDuration,
  clipEnd,
  clipFrameCaptureTarget,
  clipSpeed,
  clipVersionId,
  isAnimaticTrack,
  timelineDuration,
  trackVolume,
} from "./timeline-model"
import type { TimelineClip, TimelineDocument, TimelineTrack } from "./types"

export { VIDEO_CAPTURE_UNAVAILABLE } from "./capture-video-frame"

export type EngineSources = {
  /** version_id → URL видеофайла версии. */
  versionUrls: Record<number, string>
  /** audio_asset_id → URL финального mp3 (same-origin прокси — пригоден для fetch). */
  assetUrls: Record<number, string>
  /**
   * version_id → same-origin URL для fetch+decode звука версии (audio scrub).
   * Не versionUrls: stream_url в проде — 302 на S3 без CORS, fetch по нему невозможен.
   */
  scrubUrls?: Record<number, string>
  /**
   * version_id → same-origin proxy для offscreen video → canvas frame grab (markups).
   * Не size-gated как scrubUrls; нужен когда stream_url taints canvas (S3 redirect).
   */
  captureUrls?: Record<number, string>
  /**
   * scrub/asset URL → длительность исходного файла, мс. Пре-гейт декода PCM:
   * источник без известной длительности при скрабе не фетчится вовсе.
   */
  scrubDurationsMs?: Record<string, number>
  /** Stream URL исходника аниматика шота (общий для всех аниматик-дорожек). */
  animaticUrl?: string | null
  /** animatic_segment_id → stream URL. Для ленты из нескольких шотов; иначе хватает animaticUrl. */
  animaticUrls?: Record<number, string>
  /** Same-origin proxy для скраба аниматика. */
  animaticScrubUrl?: string | null
  /** animatic_segment_id → scrub URL. */
  animaticScrubUrls?: Record<number, string>
}

export type EngineListener = {
  onTime?: (ms: number) => void
  onPlayingChange?: (playing: boolean) => void
  /** Активное видео буферизуется во время воспроизведения (для индикатора загрузки). */
  onBuffering?: (buffering: boolean) => void
  /** Реальная длительность источника из loadedmetadata (для уточнения дефолтного таймлайна). */
  onVersionDuration?: (versionId: number, durationMs: number) => void
  /** Натуральный размер кадра видео из loadedmetadata. */
  onVersionMediaSize?: (versionId: number, size: { width: number; height: number }) => void
  /** Воспроизведение вперёд дошло до конца и цикла нет. Пауза и реверс до нуля его не вызывают. */
  onEnded?: () => void
  /**
   * play() со звуком отклонён (нет пользовательского жеста). Движок уже перешёл
   * в master mute и повторил play() без звука — UI синхронизирует кнопку.
   */
  onAutoplayBlocked?: () => void
}

/** Дрейф вторичных медиа, после которого начинается подстройка скоростью. */
const DRIFT_TOLERANCE_MS = 80
/** Дрейф, после которого элемент жёстко сикается (разрыв слишком большой для подстройки). */
const HARD_SEEK_DRIFT_MS = 300
/** Дрейф, при котором подстройка скоростью выключается (гистерезис с DRIFT_TOLERANCE_MS). */
const NUDGE_STOP_MS = 25
/** Относительная величина подстройки playbackRate (±4% — на слух незаметно). */
const NUDGE_RATE = 0.04
/** Задержка перед показом индикатора буферизации, чтобы не мигал на коротких подгрузках. */
const BUFFERING_DEBOUNCE_MS = 150
/**
 * Кадр уже отрисован (HAVE_CURRENT_DATA) — мигать не на чем, держим его дольше:
 * спиннер поверх нормальной картинки читается как «сломалось».
 */
const BUFFERING_DEBOUNCE_WITH_FRAME_MS = 700
/**
 * Окно после пользовательского сика / старта play, в котором спиннер подавлен.
 * Сразу после play() мастер ещё paused (промис play не разрешился), а readyState
 * низкий — без этого окна спиннер вспыхивал бы на каждом запуске.
 */
const SEEK_BUFFERING_GRACE_MS = 1200
/** Разница, ниже которой сик считается уже выполненным (полкадра при 60fps). */
const SEEK_EPSILON_MS = 8
/** Шаг обновления кадра при реверсе (media-элементы не играют назад). */
const REVERSE_SEEK_INTERVAL_MS = 100
/**
 * За сколько мс до того, как скрытая дорожка станет активной, запускать её
 * воспроизведение (прогрев декодера — переход между клипами без чёрного кадра).
 */
const PREROLL_MS = 400
/** Троттлинг парковочных сиков запаркованных (пауза) элементов. */
const PARK_SEEK_INTERVAL_MS = 500
/**
 * Safari при preload="metadata" залипает на HAVE_METADATA и не декодирует ни одного
 * кадра, пока элемент не сикнули, — вьюпорт остаётся чёрным до первого play (Chrome
 * нулевой кадр рисует сам). Плейхед при создании элемента обычно стоит на 0, а
 * applyFrame сик пропускает — элемент «уже на месте», — поэтому кадр запрашиваем
 * явно, микросиком. До loadedmetadata запись currentTime это default playback start
 * position: media-стек применит её сам, когда метаданные догрузятся. 1 мс попадает
 * в нулевой кадр при любом fps, так что картинка ровно та же.
 */
const FIRST_FRAME_PRIME_MS = 1

/** iOS Safari: нестандартный нативный фулскрин видеоэлемента (системный плеер). */
type WebkitFullscreenVideo = HTMLVideoElement & { webkitEnterFullscreen?: () => void }

type MediaEntry = {
  key: string
  el: HTMLVideoElement | HTMLAudioElement
  track: TimelineTrack
  /** Для custom_audio: элемент обслуживает клипы конкретного ассета. */
  assetId?: number
  /** Для video/version_audio: элемент обслуживает клипы конкретной эффективной версии. */
  versionId?: number
  isVideo: boolean
  lastReverseSeekAt: number
  lastParkSeekAt: number
  /** Текущее направление подстройки скоростью (-1 замедляем, 1 ускоряем, 0 нет). */
  nudge: -1 | 0 | 1
  /** Кэш последних применённых значений — в DOM пишем только при изменении. */
  lastVisible?: boolean
  lastMuted?: boolean
  lastVolume?: number
  /** Коалесинг сиков: цель, которая применится после текущего `seeked`. */
  pendingSeekMs?: number
  /** Висит ли слушатель `seeked` для слива pendingSeekMs. */
  seekDrainAttached?: boolean
}

function coveringClip(clips: TimelineClip[], t: number): TimelineClip | undefined {
  return clips.find((c) => t >= c.timeline_start_ms && t < clipEnd(c))
}

function clipsForMediaEntry(entry: { track: TimelineTrack; assetId?: number; versionId?: number }): TimelineClip[] {
  if (isAnimaticTrack(entry.track)) return entry.track.clips
  if (entry.track.kind === "custom_audio") {
    return entry.track.clips.filter((c) => c.audio_asset_id === entry.assetId)
  }
  return entry.track.clips.filter((c) => clipVersionId(entry.track, c) === entry.versionId)
}

function videoEntryKey(track: TimelineTrack, clip: TimelineClip): string {
  return isAnimaticTrack(track) ? `v:${track.id}:animatic` : `v:${track.id}:${clipVersionId(track, clip)}`
}

function fadeGain(clip: TimelineClip, t: number): number {
  const local = t - clip.timeline_start_ms
  // Фейды заданы в timeline-мс — длина клипа с учётом скорости.
  const len = clipDuration(clip)
  let gain = 1
  const fadeIn = clip.fade_in_ms ?? 0
  const fadeOut = clip.fade_out_ms ?? 0
  if (fadeIn > 0 && local < fadeIn) gain = Math.min(gain, local / fadeIn)
  if (fadeOut > 0 && local > len - fadeOut) gain = Math.min(gain, (len - local) / fadeOut)
  return Math.max(0, Math.min(1, gain))
}

/**
 * Движок воспроизведения мультитрек-таймлайна: пул скрытых media-элементов
 * (по одному на дорожку; для custom_audio — на пару дорожка+ассет).
 * Видео стекируются в вьюпорте, виден клип самой верхней непустой дорожки.
 *
 * Часы: при прямом воспроизведении мастером является активный видеоэлемент —
 * время таймлайна выводится из его currentTime, поэтому видимое видео никогда
 * не сикается на ходу (сик = микропауза декодера). Wall clock (performance.now)
 * служит fallback'ом: зазоры без видео, реверс, чисто аудио-таймлайны.
 * Вторичные элементы (version_audio, скрытые видео) подтягиваются к мастеру
 * подстройкой playbackRate, жёсткий сик — только при большом разрыве.
 *
 * Экономия декодеров: невидимые и неслышимые элементы паркуются (пауза +
 * редкий сик на нужную позицию) вместо непрерывного декодирования; за
 * PREROLL_MS до активации скрытая видеодорожка запускается заранее.
 *
 * Сики коалесятся: пока элемент занят (`el.seeking`), новые цели только
 * запоминаются, а по `seeked` применяется последняя. Иначе перетаскивание
 * плейхеда (120 событий/с) превращалось бы в очередь сетевых Range-запросов,
 * и кадр догонял бы курсор через несколько секунд.
 */
export class TimelinePlaybackEngine {
  private viewport: HTMLElement
  private doc: TimelineDocument = { schema_version: 1, tracks: [] }
  private sources: EngineSources = { versionUrls: {}, assetUrls: {} }
  private entries = new Map<string, MediaEntry>()
  private listener: EngineListener

  private baseMs = 0
  private baseWall = 0
  private playing = false
  private rate = 1
  private loop = false
  private masterVolume = 1
  private masterMuted = false
  /** NotAllowedError уже обработан: повторный play() не должен снова глушить и звать слушателя. */
  private autoplayFallbackUsed = false
  private rafId: number | null = null
  private reportedDurations = new Set<number>()
  private reportedMediaSizes = new Set<number>()
  private bufferingSince: number | null = null
  private bufferingReported = false
  private scrubbing = false
  private scrubTargetMs: number | null = null
  private scrubRafId: number | null = null
  /** Слышимый скраб (гранулы Web Audio); контекст и PCM-кэш — модульные, общие для движков. */
  private scrubber = new AudioScrubber()
  private audioScrubEnabled = true
  /** Есть ли хоть один fetch-able источник звука — без него AudioContext не создаём. */
  private scrubSourcesAvailable = false
  /** До этого момента (performance.now) спиннер подавлен — держим последний кадр. */
  private seekGraceUntil = 0
  /** Элемент, отданный системному iOS-плееру (webkitEnterFullscreen) — движок его не трогает. */
  private nativeFsEl: HTMLVideoElement | null = null
  /**
   * Элементы, созданные или сменившие src в последнем setTimeline.
   * Пока идёт проигрывание, сикаем только их: чужой новый файл не должен
   * ставить на паузу уже играющий шот.
   */
  private freshKeys = new Set<string>()

  constructor(viewport: HTMLElement, listener: EngineListener = {}) {
    this.viewport = viewport
    this.listener = listener
  }

  // --- Публичное API ---

  setTimeline(doc: TimelineDocument, sources: EngineSources) {
    this.doc = doc
    this.sources = sources
    this.scrubSourcesAvailable =
      Object.keys(sources.assetUrls).length > 0 ||
      Object.keys(sources.scrubUrls ?? {}).length > 0 ||
      Boolean(sources.animaticScrubUrl) ||
      Object.values(sources.animaticScrubUrls ?? {}).some((url) => Boolean(url))
    this.syncElements()
    // На паузе сикаем всех: playhead мог уехать, а элементы ещё на нуле.
    // Во время play — только freshKeys (новый src), играющий файл не трогаем.
    this.applyFrame(this.currentMs(), { forceSeek: !this.playing })
    this.freshKeys.clear()
  }

  currentMs(): number {
    if (!this.playing) return this.baseMs
    return Math.max(0, this.baseMs + (performance.now() - this.baseWall) * this.rate)
  }

  durationMs(): number {
    return timelineDuration(this.doc)
  }

  isPlaying(): boolean {
    return this.playing
  }

  /** Идёт перетаскивание плейхеда (между scrubTo и endScrub). */
  isScrubbing(): boolean {
    return this.scrubbing
  }

  getRate(): number {
    return this.rate
  }

  /**
   * Цель серверного захвата кадра: версия клипа под плейхедом и позиция в её
   * исходнике (source-мс, с учётом скорости клипа). null — зазор, скрытый слой
   * или аниматик (кадр из аниматика бэкенд не извлекает).
   */
  captureTarget(): { versionId: number | null; sourceMs: number } | null {
    // Тот же кламп, что в applyFrame и enterNativeVideoFullscreen: доигравший до
    // конца плейхед стоит ровно на durationMs, а coveringClip ищет по полуинтервалу
    // и вернул бы null — кнопка гасла бы именно на последнем кадре.
    const duration = this.durationMs()
    const raw = this.currentMs()
    const t = duration > 0 ? Math.min(raw, duration - 1) : raw
    const active = this.activeVideoAt(t)
    if (!active) return null
    return clipFrameCaptureTarget(active.track, active.clip, t)
  }

  /**
   * Снимок кадра под плейхедом (для разметки / эскиза).
   * Предпочитает same-origin captureUrls (proxy): stream_url 302→S3 taints canvas.
   */
  async captureCurrentFrame(): Promise<HTMLImageElement> {
    this.pause()
    const target = this.captureTarget()
    if (!target) throw new Error("Video frame unavailable")

    const captureUrl = target.versionId != null ? this.sources.captureUrls?.[target.versionId]?.trim() : undefined
    if (captureUrl) {
      return captureFrameFromCorsVideo(captureUrl, target.sourceMs / 1000)
    }

    const el = this.applyFrame(this.currentMs(), { forceSeek: true })
    if (!(el instanceof HTMLVideoElement)) {
      throw new Error("Video frame unavailable")
    }
    if (el.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
      await new Promise<void>((resolve, reject) => {
        const onReady = () => {
          el.removeEventListener("seeked", onReady)
          el.removeEventListener("loadeddata", onReady)
          el.removeEventListener("error", onError)
          resolve()
        }
        const onError = () => {
          el.removeEventListener("seeked", onReady)
          el.removeEventListener("loadeddata", onReady)
          el.removeEventListener("error", onError)
          reject(new Error("Video frame unavailable"))
        }
        el.addEventListener("seeked", onReady)
        el.addEventListener("loadeddata", onReady)
        el.addEventListener("error", onError)
      })
    }
    const w = el.videoWidth
    const h = el.videoHeight
    if (!w || !h) throw new Error("Video frame unavailable")
    const canvas = document.createElement("canvas")
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext("2d")
    if (!ctx) throw new Error("Canvas unavailable")
    try {
      ctx.drawImage(el, 0, 0, w, h)
    } catch {
      throw new Error(VIDEO_CAPTURE_UNAVAILABLE)
    }
    let blob: Blob | null = null
    try {
      blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png", 0.92))
    } catch {
      throw new Error(VIDEO_CAPTURE_UNAVAILABLE)
    }
    if (!blob) throw new Error(VIDEO_CAPTURE_UNAVAILABLE)
    const url = URL.createObjectURL(blob)
    try {
      return await new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve(img)
        img.onerror = () => reject(new Error("Frame capture failed"))
        img.src = url
      })
    } finally {
      URL.revokeObjectURL(url)
    }
  }

  play(rate = 1) {
    this.endScrub()
    // Недокачанный PCM скраба отменяем: скачивание и декод не должны конкурировать
    // за сеть/CPU/память с реальным воспроизведением. Готовый кэш не трогаем.
    this.scrubber.cancelLoads()
    this.baseMs = this.currentMs()
    if (this.playing && this.rate === rate) return
    if (rate > 0 && this.baseMs >= this.durationMs() - 1) {
      // Рестарт с конца: одного сброса часов мало — медиа-элементы всё ещё стоят
      // на конце (el.ended), и rebaseToActiveVideo вернул бы плейхед обратно.
      this.baseMs = 0
      this.applyFrame(0, { forceSeek: true })
      this.listener.onTime?.(0)
    }
    this.rate = rate
    this.baseWall = performance.now()
    this.playing = true
    this.bufferingSince = null
    this.seekGraceUntil = performance.now() + SEEK_BUFFERING_GRACE_MS
    this.listener.onPlayingChange?.(true)
    this.startTicking()
  }

  pause() {
    if (!this.playing) return
    this.baseMs = this.currentMs()
    this.playing = false
    this.pauseAllMedia()
    this.applyFrame(this.baseMs, { forceSeek: true })
    this.clearBuffering()
    this.listener.onPlayingChange?.(false)
  }

  toggle() {
    if (this.playing) this.pause()
    else this.play(this.rate > 0 ? this.rate : 1)
  }

  seek(ms: number) {
    this.cancelScrub()
    this.baseMs = Math.max(0, Math.min(ms, this.durationMs()))
    this.baseWall = performance.now()
    this.seekGraceUntil = performance.now() + SEEK_BUFFERING_GRACE_MS
    this.applyFrame(this.baseMs, { forceSeek: true })
    this.listener.onTime?.(this.baseMs)
  }

  /**
   * Перетаскивание плейхеда. Часы и onTime обновляются сразу (плейхед не отстаёт
   * от курсора), а applyFrame — не чаще раза за кадр анимации; внутри сик ещё и
   * коалесится в requestSeek. Предполагает паузу: вызывающие ставят её на pointerdown.
   */
  scrubTo(ms: number) {
    // Разблокировка AudioContext обязана случиться синхронно в pointer-обработчике:
    // rAF-колбэк Safari стеком пользовательского жеста не считает.
    if (this.audioScrubEnabled && !this.playing && this.scrubSourcesAvailable) this.scrubber.ensureRunning()
    const clamped = Math.max(0, Math.min(ms, this.durationMs()))
    this.baseMs = clamped
    this.baseWall = performance.now()
    this.scrubTargetMs = clamped
    this.seekGraceUntil = performance.now() + SEEK_BUFFERING_GRACE_MS
    this.listener.onTime?.(clamped)
    // Во время скраба спиннер не показываем: сик пользователя — не буферизация.
    if (!this.scrubbing) {
      this.scrubbing = true
      this.clearBuffering()
    }
    if (this.scrubRafId != null) return
    this.scrubRafId = requestAnimationFrame(() => {
      this.scrubRafId = null
      if (this.scrubTargetMs != null) this.applyFrame(this.scrubTargetMs, { forceSeek: true })
    })
  }

  /** Конец перетаскивания: финальный точный сик на последнюю позицию. */
  endScrub() {
    if (!this.scrubbing) return
    const target = this.scrubTargetMs
    this.cancelScrub()
    this.seekGraceUntil = performance.now() + SEEK_BUFFERING_GRACE_MS
    if (target != null) this.applyFrame(target, { forceSeek: true })
  }

  private cancelScrub() {
    this.scrubbing = false
    this.scrubTargetMs = null
    if (this.scrubRafId != null) cancelAnimationFrame(this.scrubRafId)
    this.scrubRafId = null
    // Единая точка выхода из скраба (endScrub, seek, play, iOS-фулскрин, dispose) —
    // рамп-аут гранул здесь покрывает и путь AnimaticTimeline, который endScrub не зовёт.
    this.scrubber.stop()
  }

  setLoop(loop: boolean) {
    this.loop = loop
  }

  setMasterVolume(volume: number) {
    this.masterVolume = Math.max(0, Math.min(1, volume))
  }

  setMasterMuted(muted: boolean) {
    this.masterMuted = muted
    if (!muted) this.autoplayFallbackUsed = false
  }

  setAudioScrub(enabled: boolean) {
    this.audioScrubEnabled = enabled
    if (!enabled) {
      this.scrubber.stop()
      this.scrubber.cancelLoads()
    }
  }

  /**
   * iPhone Safari: Fullscreen API для произвольных элементов недоступен — единственный
   * нативный фулскрин это системный видеоплеер (webkitEnterFullscreen), как у YouTube.
   * Сначала открываем системный плеер (элемент продолжает играть бесшовно), затем
   * отдаём ему управление: свой цикл останавливаем, остальные медиа глушим, элемент
   * на время handoff'а исключаем из applyFrame. По webkitendfullscreen забираем
   * управление: позиция элемента маппится обратно в таймлайн-время.
   * Ограничение: системный плеер играет исходный файл целиком — подрезка клипов и
   * остальные дорожки на время нативного фулскрина не действуют.
   */
  enterNativeVideoFullscreen(): boolean {
    if (this.nativeFsEl) return true
    const duration = this.durationMs()
    const t = this.currentMs()
    const active = this.activeVideoAt(duration > 0 ? Math.min(t, duration - 1) : t)
    const entry = active?.entry
    if (!entry || !(entry.el instanceof HTMLVideoElement)) return false
    const el: WebkitFullscreenVideo = entry.el
    if (typeof el.webkitEnterFullscreen !== "function") return false
    try {
      el.webkitEnterFullscreen()
    } catch (err) {
      // Метаданные ещё не догрузились (InvalidStateError) — фулскрин не открылся,
      // состояние движка не тронуто.
      console.warn("webkitEnterFullscreen failed", err)
      return false
    }

    // Handoff: часы фиксируем, цикл останавливаем, элемент больше не трогаем.
    this.nativeFsEl = el
    entry.pendingSeekMs = undefined
    this.baseMs = this.currentMs()
    this.playing = false
    if (this.rafId != null) cancelAnimationFrame(this.rafId)
    this.rafId = null
    this.cancelScrub()
    // Нативный плеер — то же «реальное воспроизведение», что и play():
    // недокачанный PCM скраба не должен конкурировать с его стримом (тем более на iOS).
    this.scrubber.cancelLoads()
    for (const other of this.entries.values()) {
      if (other.el !== el && !other.el.paused) other.el.pause()
    }
    this.clearBuffering()
    this.listener.onPlayingChange?.(false)

    const onEnd = () => {
      el.removeEventListener("webkitendfullscreen", onEnd)
      this.nativeFsEl = null
      // Системный плеер по выходу сам ставит паузу; возвращаем плейхед движка туда,
      // куда пользователь досмотрел/досикался в нативном плеере.
      this.seek(this.timelineMsFromSource(entry, el.currentTime * 1000))
    }
    el.addEventListener("webkitendfullscreen", onEnd)
    return true
  }

  /** Позиция источника → таймлайн-время: по клипам элемента, с клампом в границы ближайшего. */
  private timelineMsFromSource(entry: MediaEntry, sourceMs: number): number {
    const clips = clipsForMediaEntry(entry)
    let best: { t: number; dist: number } | null = null
    for (const clip of clips) {
      const clamped = Math.max(clip.source_in_ms, Math.min(sourceMs, clip.source_out_ms))
      const dist = Math.abs(clamped - sourceMs)
      const t = clip.timeline_start_ms + (clamped - clip.source_in_ms) / clipSpeed(clip)
      if (!best || dist < best.dist) best = { t, dist }
    }
    return best ? best.t : this.currentMs()
  }

  dispose() {
    if (this.rafId != null) cancelAnimationFrame(this.rafId)
    this.rafId = null
    this.cancelScrub()
    this.scrubber.cancelLoads()
    for (const entry of this.entries.values()) entry.el.remove()
    this.entries.clear()
  }

  // --- Коалесинг сиков ---

  /**
   * Единственный путь записи currentTime. Пока элемент занят сиком, новая цель
   * только запоминается — по `seeked` применяется последняя (latest-target-wins).
   * Так драг плейхеда стоит один сик на кадр анимации, а не один на pointermove.
   */
  private requestSeek(entry: MediaEntry, wantMs: number) {
    const el = entry.el
    if (el.seeking) {
      entry.pendingSeekMs = wantMs
      return
    }
    entry.pendingSeekMs = undefined
    if (Math.abs(el.currentTime * 1000 - wantMs) < SEEK_EPSILON_MS) return
    this.attachSeekDrain(entry)
    el.currentTime = wantMs / 1000
  }

  private attachSeekDrain(entry: MediaEntry) {
    if (entry.seekDrainAttached) return
    entry.seekDrainAttached = true
    const el = entry.el
    const onSeeked = () => {
      const next = entry.pendingSeekMs
      entry.pendingSeekMs = undefined
      if (next != null && Math.abs(el.currentTime * 1000 - next) >= SEEK_EPSILON_MS) {
        el.currentTime = next / 1000
        return
      }
      el.removeEventListener("seeked", onSeeked)
      entry.seekDrainAttached = false
    }
    el.addEventListener("seeked", onSeeked)
  }

  // --- Пул media-элементов ---

  private animaticUrlFor(track: TimelineTrack): string | null {
    const segmentId = track.animatic_segment_id
    if (segmentId != null) {
      const mapped = this.sources.animaticUrls?.[segmentId]
      if (mapped) return mapped
    }
    return this.sources.animaticUrl ?? null
  }

  private animaticScrubUrlFor(track: TimelineTrack): string | undefined {
    const segmentId = track.animatic_segment_id
    if (segmentId != null) {
      const mapped = this.sources.animaticScrubUrls?.[segmentId]
      if (mapped) return mapped
    }
    return this.sources.animaticScrubUrl ?? undefined
  }

  private entryKeys(
    doc: TimelineDocument,
  ): Map<string, { track: TimelineTrack; assetId?: number; versionId?: number; url: string | null }> {
    const keys = new Map<string, { track: TimelineTrack; assetId?: number; versionId?: number; url: string | null }>()
    for (const track of doc.tracks) {
      if (isAnimaticTrack(track)) {
        const prefix = track.kind === "video" ? "v" : "ca"
        keys.set(`${prefix}:${track.id}:animatic`, { track, url: this.animaticUrlFor(track) })
        continue
      }
      if (track.kind === "video" || track.kind === "version_audio") {
        // Пул per-(дорожка, эффективная версия): клипы с per-clip override играют своим элементом.
        const versionIds = new Set<number>()
        for (const clip of track.clips) {
          const versionId = clipVersionId(track, clip)
          if (versionId != null) versionIds.add(versionId)
        }
        if (versionIds.size === 0 && track.shot_media_version_id != null) versionIds.add(track.shot_media_version_id)
        for (const versionId of versionIds) {
          keys.set(`${track.kind === "video" ? "v" : "va"}:${track.id}:${versionId}`, {
            track,
            versionId,
            url: this.sources.versionUrls[versionId] ?? null,
          })
        }
      } else {
        const assetIds = [...new Set(track.clips.map((c) => c.audio_asset_id).filter((id): id is number => id != null))]
        for (const assetId of assetIds) {
          keys.set(`ca:${track.id}:${assetId}`, { track, assetId, url: this.sources.assetUrls[assetId] ?? null })
        }
      }
    }
    return keys
  }

  private syncElements(): boolean {
    const wanted = this.entryKeys(this.doc)
    let urlChanged = false

    for (const [key, entry] of this.entries) {
      if (!wanted.has(key)) {
        entry.el.remove()
        this.entries.delete(key)
      }
    }

    for (const [key, spec] of wanted) {
      if (!spec.url) continue
      const existing = this.entries.get(key)
      if (existing) {
        existing.track = spec.track
        if (existing.el.getAttribute("src") !== spec.url) {
          existing.el.src = spec.url
          this.freshKeys.add(key)
          urlChanged = true
          // Смена src сбрасывает элемент в HAVE_NOTHING — кадр снова просим явно.
          if (existing.el instanceof HTMLVideoElement) existing.el.currentTime = FIRST_FRAME_PRIME_MS / 1000
          // Новый файл — разрешаем заново сообщить loadedmetadata duration.
          if (spec.versionId != null) {
            this.reportedDurations.delete(spec.versionId)
            this.reportedMediaSizes.delete(spec.versionId)
          }
        }
        continue
      }

      const isVideo = spec.track.kind === "video"
      // version_audio и custom_audio играем <audio>-элементом: для mp4 браузер
      // декодирует только звуковую дорожку, не тратя ресурсы на видеокадры.
      const el = document.createElement(isVideo ? "video" : "audio")
      el.src = spec.url
      this.freshKeys.add(key)
      urlChanged = true
      // "metadata", не "auto": auto заставляет браузер выкачивать большие файлы целиком
      // (аниматик на сотни МБ). Буферизация при воспроизведении работает и так.
      // Без crossOrigin: кадры движка не рисуются в canvas, зато src может быть
      // redirect на S3 без CORS-настройки бакета.
      el.preload = "metadata"
      if (el instanceof HTMLVideoElement) {
        el.playsInline = true
        el.currentTime = FIRST_FRAME_PRIME_MS / 1000
        el.style.position = "absolute"
        el.style.inset = "0"
        el.style.width = "100%"
        el.style.height = "100%"
        el.style.objectFit = "contain"
      } else {
        el.style.display = "none"
      }

      if (isVideo && spec.versionId != null) {
        const versionId = spec.versionId
        const reportMedia = () => {
          if (!(el instanceof HTMLVideoElement)) return
          if (Number.isFinite(el.duration) && el.duration > 0 && !this.reportedDurations.has(versionId)) {
            this.reportedDurations.add(versionId)
            this.listener.onVersionDuration?.(versionId, Math.round(el.duration * 1000))
          }
          if (!this.reportedMediaSizes.has(versionId) && el.videoWidth > 0 && el.videoHeight > 0) {
            this.reportedMediaSizes.add(versionId)
            this.listener.onVersionMediaSize?.(versionId, { width: el.videoWidth, height: el.videoHeight })
          }
        }
        el.addEventListener("loadedmetadata", reportMedia)
        el.addEventListener("loadeddata", reportMedia)
      }

      this.viewport.appendChild(el)
      this.entries.set(key, {
        key,
        el,
        track: spec.track,
        assetId: spec.assetId,
        versionId: spec.versionId,
        isVideo,
        lastReverseSeekAt: 0,
        lastParkSeekAt: 0,
        nudge: 0,
      })
    }
    return urlChanged
  }

  private pauseAllMedia() {
    for (const entry of this.entries.values()) {
      if (entry.el === this.nativeFsEl) continue
      if (!entry.el.paused) entry.el.pause()
    }
  }

  // --- Активное видео (мастер-часы при прямом воспроизведении) ---

  private activeVideoAt(
    t: number,
  ): { key: string; entry: MediaEntry | null; track: TimelineTrack; clip: TimelineClip } | null {
    for (const track of this.doc.tracks) {
      if (track.kind !== "video" || track.hidden) continue
      const clip = coveringClip(track.clips, t)
      if (!clip) continue
      const key = videoEntryKey(track, clip)
      return { key, entry: this.entries.get(key) ?? null, track, clip }
    }
    return null
  }

  /**
   * Подводит wall clock под фактическое время активного видео. Пока мастер
   * не готов (буфер / сик / play ещё не стартовал), wall clock замораживается —
   * плейхед стоит вместе с кадром вместо того, чтобы убегать вперёд.
   * Если медиа уже ended, а таймлайн ещё «длиннее» — дотягиваем playhead до
   * конца клипа/таймлайна, иначе часы зависают навсегда (el.ended + hold).
   */
  private rebaseToActiveVideo(active: ReturnType<TimelinePlaybackEngine["activeVideoAt"]>) {
    if (this.rate <= 0) return
    const t = this.currentMs()
    // Gap / audio-only — wall clock идёт сам.
    if (!active) return
    // Клип есть, медиаэлемента нет (кадр или чёрный экран без URL): ждать нечего.
    if (!active.entry) return

    const el = active.entry.el
    if (el?.ended) {
      // Видео закончилось раньше document duration (рассинхрон ffprobe / web_preview).
      this.baseMs = Math.min(this.durationMs(), Math.max(t, clipEnd(active.clip)))
      this.baseWall = performance.now()
      return
    }
    // Мастер ещё не отдаёт время — часы стоят на времени прошлого тика.
    if (!el || el.seeking || el.paused || el.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
      this.holdClock(active.clip)
      return
    }

    const tVideo =
      active.clip.timeline_start_ms + (el.currentTime * 1000 - active.clip.source_in_ms) / clipSpeed(active.clip)
    // Элемент ещё не спозиционирован на клип (только что вошли) — тоже hold,
    // иначе плейхед убежит, пока идёт первичный сик.
    if (Math.abs(tVideo - t) > HARD_SEEK_DRIFT_MS) {
      this.holdClock(active.clip)
      return
    }

    this.baseMs = tVideo
    this.baseWall = performance.now()
  }

  /**
   * Заморозка часов, пока мастер не готов. baseMs — время прошлого тика (см. конец
   * tick), но не раньше начала клипа: иначе плейхед откатывался бы в зазор перед
   * клипом, мастер пропадал, и вход в клип зацикливался.
   */
  private holdClock(clip: TimelineClip) {
    this.baseMs = Math.max(this.baseMs, clip.timeline_start_ms)
    this.baseWall = performance.now()
  }

  // --- Индикатор буферизации ---

  private updateBuffering(activeEl: HTMLVideoElement | HTMLAudioElement | null) {
    if (this.scrubbing) {
      this.clearBuffering()
      return
    }
    const el = this.rate > 0 ? activeEl : null
    // paused: play() ещё не стартовал / автоплей заблокирован — часы на hold.
    const stalled = el != null && (el.seeking || el.paused || el.readyState < HTMLMediaElement.HAVE_FUTURE_DATA)
    if (!stalled) {
      this.clearBuffering()
      return
    }
    const now = performance.now()
    // Кадр отрисован и мы сразу после сика/play — это нормальный старт, не залипание.
    const hasFrame = el != null && el.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA
    if (hasFrame && now < this.seekGraceUntil) {
      this.clearBuffering()
      return
    }
    if (this.bufferingSince == null) this.bufferingSince = now
    const debounce = hasFrame ? BUFFERING_DEBOUNCE_WITH_FRAME_MS : BUFFERING_DEBOUNCE_MS
    if (now - this.bufferingSince >= debounce) this.setBuffering(true)
  }

  private clearBuffering() {
    this.bufferingSince = null
    this.setBuffering(false)
  }

  private setBuffering(buffering: boolean) {
    if (this.bufferingReported === buffering) return
    this.bufferingReported = buffering
    this.listener.onBuffering?.(buffering)
  }

  // --- Tick ---

  private startTicking() {
    if (this.rafId != null) return
    const tick = () => {
      this.rafId = null
      if (!this.playing) return
      this.rebaseToActiveVideo(this.activeVideoAt(this.currentMs()))
      let t = this.currentMs()
      const duration = this.durationMs()

      if (this.rate > 0 && t >= duration) {
        if (this.loop && duration > 0) {
          this.seek(0)
          this.baseWall = performance.now()
        } else {
          this.baseMs = duration
          this.playing = false
          this.pauseAllMedia()
          this.applyFrame(duration, { forceSeek: true })
          this.clearBuffering()
          this.listener.onPlayingChange?.(false)
          this.listener.onTime?.(duration)
          this.listener.onEnded?.()
          return
        }
      }
      if (this.rate < 0 && t <= 0) {
        this.baseMs = 0
        this.playing = false
        this.pauseAllMedia()
        this.applyFrame(0, { forceSeek: true })
        this.clearBuffering()
        this.listener.onPlayingChange?.(false)
        this.listener.onTime?.(0)
        return
      }

      t = this.currentMs()
      // Якорь часов — всегда время текущего тика: hold в rebaseToActiveVideo
      // замораживает именно его, а не устаревшую базу (после зазора она отставала).
      this.baseWall += (t - this.baseMs) / this.rate
      this.baseMs = t
      const activeEl = this.applyFrame(t, { forceSeek: false })
      this.updateBuffering(activeEl)
      this.listener.onTime?.(t)
      this.rafId = requestAnimationFrame(tick)
    }
    this.rafId = requestAnimationFrame(tick)
  }

  // --- Применение состояния к элементам ---

  /** Слышим ли трек с учётом mute-флагов, скрытых слоёв и detached audio. */
  private isAudible(track: TimelineTrack): boolean {
    if (track.muted || this.masterMuted) return false
    // Скрытый видеослой молчит вместе с картинкой; откреплённое аудио живёт
    // на своей version_audio-дорожке и от «глаза» видео не зависит.
    if (track.kind === "video" && (track.audio_detached || track.hidden || track.animatic_mode === "video_silent")) {
      return false
    }
    return true
  }

  /** Возвращает элемент активного видео (для индикатора буферизации). */
  private applyFrame(t: number, { forceSeek }: { forceSeek: boolean }): HTMLVideoElement | HTMLAudioElement | null {
    // На самом конце таймлайна (t === duration) эксклюзивный конец клипа дал бы
    // «нет активного клипа» и чёрный экран — держим последний кадр.
    const duration = this.durationMs()
    const frameT = duration > 0 ? Math.min(t, duration - 1) : t
    const active = this.activeVideoAt(frameT)
    const activeVideoKey = active?.key ?? null
    // Прогрев: кто станет активным видео через PREROLL_MS (только при игре вперёд).
    const prerollVideoKey =
      this.playing && this.rate > 0 ? (this.activeVideoAt(frameT + PREROLL_MS * this.rate)?.key ?? null) : null
    // Голоса слышимого скраба: media-элементы на паузе дают кадр, гранулы — звук.
    // При playing-скрабе (фулскрин-ползунок без паузы) звук и так идёт с элементов.
    const collectVoices = this.scrubbing && !this.playing && this.audioScrubEnabled && this.scrubSourcesAvailable
    const scrubVoices: (ScrubVoice & { trackIndex: number })[] = []
    // Индексы дорожек для сортировки голосов — одним проходом, не indexOf на entry.
    const trackIndexes = collectVoices ? new Map(this.doc.tracks.map((track, i) => [track, i])) : null

    for (const entry of this.entries.values()) {
      // Элемент в системном iOS-фулскрине: им управляет нативный плеер, не мы.
      if (entry.el === this.nativeFsEl) continue
      const clips = clipsForMediaEntry(entry)
      const clip = coveringClip(clips, frameT)

      if (entry.isVideo) {
        const visible = entry.key === activeVideoKey
        if (entry.lastVisible !== visible) {
          entry.el.style.visibility = visible ? "visible" : "hidden"
          entry.lastVisible = visible
        }
      }

      if (!clip) {
        // Прогрев дорожки, чей клип начнётся в ближайшие PREROLL_MS.
        if (entry.isVideo && entry.key === prerollVideoKey && this.playing && this.rate > 0) {
          const nextClip = coveringClip(clips, frameT + PREROLL_MS * this.rate)
          if (nextClip) {
            this.warmUp(entry, nextClip.source_in_ms)
            continue
          }
        }
        if (!entry.el.paused) entry.el.pause()
        continue
      }

      const speed = clipSpeed(clip)
      const wantMs = clip.source_in_ms + (frameT - clip.timeline_start_ms) * speed
      const seekThis = forceSeek || this.freshKeys.has(entry.key)
      const isMaster = entry.isVideo && entry.key === activeVideoKey
      // Невидимое и неслышимое видео не декодируем: паркуем (пауза + редкий сик),
      // кроме окна preroll перед его активацией.
      const parked =
        entry.isVideo && !isMaster && !this.isAudible(entry.track) && entry.key !== prerollVideoKey && !seekThis
      const volume = this.applyAudio(entry, clip, frameT)
      if (collectVoices && volume > 0) {
        const url = isAnimaticTrack(entry.track)
          ? this.animaticScrubUrlFor(entry.track)
          : entry.track.kind === "custom_audio"
            ? entry.assetId != null
              ? this.sources.assetUrls[entry.assetId]
              : undefined
            : entry.versionId != null
              ? this.sources.scrubUrls?.[entry.versionId]
              : undefined
        if (url) {
          const trackIndex = trackIndexes?.get(entry.track) ?? 0
          const durationMs = this.sources.scrubDurationsMs?.[url]
          scrubVoices.push({ url, sourceMs: wantMs, rate: speed, gain: volume, durationMs, trackIndex })
        }
      }
      this.syncMediaTime(entry, wantMs, seekThis, isMaster, parked, speed)
    }
    if (collectVoices) {
      // Приоритет полифонии — порядок дорожек документа, не порядок создания entries.
      scrubVoices.sort((a, b) => a.trackIndex - b.trackIndex)
      this.scrubber.update(frameT, scrubVoices)
    }
    return active?.entry?.el ?? null
  }

  /** Запускает элемент заранее с начала будущего клипа, чтобы декодер был готов. */
  private warmUp(entry: MediaEntry, sourceStartMs: number) {
    const el = entry.el
    if (!el.paused) return
    if (Math.abs(el.currentTime * 1000 - sourceStartMs) > HARD_SEEK_DRIFT_MS) this.requestSeek(entry, sourceStartMs)
    // На прогреве звук не нужен — applyAudio включит его, когда клип станет текущим.
    el.muted = true
    entry.lastMuted = true
    this.playEntry(entry)
  }

  /**
   * Ховер и прочий play() вне жеста браузер отклоняет, если элемент не muted.
   * Один раз глушим мастер и повторяем play(), чтобы картинка не замирала.
   * Возвращает true, если этот вызов сам выполнил повтор.
   */
  private recoverAutoplay(err: unknown, entry: MediaEntry): boolean {
    // Без слушателя UI не узнает о mute и кнопка звука соврёт: такие плееры ждут клика, как раньше.
    if (!this.listener.onAutoplayBlocked) return false
    if (this.autoplayFallbackUsed || this.masterMuted) return false
    if (!(err instanceof DOMException) || err.name !== "NotAllowedError") return false
    this.autoplayFallbackUsed = true
    this.masterMuted = true
    for (const item of this.entries.values()) {
      item.el.muted = true
      item.lastMuted = true
    }
    this.listener.onAutoplayBlocked?.()
    this.playEntry(entry)
    return true
  }

  private playEntry(entry: MediaEntry) {
    void entry.el.play().catch((err: unknown) => {
      this.recoverAutoplay(err, entry)
    })
  }

  /** Возвращает применённую громкость — она же gain голоса слышимого скраба. */
  private applyAudio(entry: MediaEntry, clip: TimelineClip, t: number): number {
    const audible = this.isAudible(entry.track)
    const muted = !audible
    if (entry.lastMuted !== muted) {
      entry.el.muted = muted
      entry.lastMuted = muted
    }
    // masterVolume — эфемерный преф прослушивания (не персистится, в рендер не попадает);
    // trackVolume — персистентная громкость дорожки, её же применяет серверный рендер.
    const volume = audible ? this.masterVolume * trackVolume(entry.track) * fadeGain(clip, t) : 0
    if (entry.lastVolume !== volume) {
      entry.el.volume = volume
      entry.lastVolume = volume
    }
    return volume
  }

  private syncMediaTime(
    entry: MediaEntry,
    wantMs: number,
    forceSeek: boolean,
    isMaster: boolean,
    parked: boolean,
    clipSpd: number,
  ) {
    const el = entry.el
    const haveMs = el.currentTime * 1000

    if (!this.playing || forceSeek) {
      if (!el.paused) el.pause()
      entry.nudge = 0
      if (Math.abs(haveMs - wantMs) > 16) this.requestSeek(entry, wantMs)
      return
    }

    if (this.rate < 0) {
      // Реверс: покадровые сики с троттлингом.
      if (!el.paused) el.pause()
      const now = performance.now()
      if (now - entry.lastReverseSeekAt >= REVERSE_SEEK_INTERVAL_MS) {
        entry.lastReverseSeekAt = now
        this.requestSeek(entry, wantMs)
      }
      return
    }

    if (parked) {
      // Парковка: элемент стоит, редкими сиками держим его около нужной позиции,
      // чтобы включение (unmute/переключение видимости) было мгновенным.
      if (!el.paused) el.pause()
      entry.nudge = 0
      const now = performance.now()
      if (now - entry.lastParkSeekAt >= PARK_SEEK_INTERVAL_MS && Math.abs(haveMs - wantMs) > HARD_SEEK_DRIFT_MS) {
        entry.lastParkSeekAt = now
        this.requestSeek(entry, wantMs)
      }
      return
    }

    if (isMaster) {
      // Мастер сам является часами — дрейфа быть не может; жёсткий сик только
      // если элемент явно не на месте (вход в клип, смена source-диапазона).
      const masterRate = this.rate * clipSpd
      if (el.playbackRate !== masterRate) el.playbackRate = masterRate
      if (Math.abs(haveMs - wantMs) > HARD_SEEK_DRIFT_MS) this.requestSeek(entry, wantMs)
      if (el.paused) this.playEntry(entry)
      return
    }

    // Вторичные медиа: большой разрыв — сик, средний — подстройка скоростью.
    const drift = haveMs - wantMs
    if (Math.abs(drift) > HARD_SEEK_DRIFT_MS) {
      this.requestSeek(entry, wantMs)
      entry.nudge = 0
    } else if (Math.abs(drift) > DRIFT_TOLERANCE_MS) {
      entry.nudge = drift > 0 ? -1 : 1
    } else if (Math.abs(drift) < NUDGE_STOP_MS) {
      entry.nudge = 0
    }
    const targetRate = this.rate * clipSpd * (1 + entry.nudge * NUDGE_RATE)
    if (el.playbackRate !== targetRate) el.playbackRate = targetRate
    // ended: play() перезапустил бы доигравший файл с нуля — щелчок звука в конце таймлайна.
    if (el.paused && !el.ended) this.playEntry(entry)
  }
}
